/**
 * Lightweight typed API client for the Playerso backend.
 *
 * In a single-origin deployment the bundle is served by the very same NestJS
 * process that exposes `/api`, so an unset (or empty) VITE_API_URL must resolve
 * to a same-origin relative path. Falling back to an absolute localhost URL
 * would make the browser call its own machine and break every request in
 * production, so the default is deliberately `/api`.
 * Always sends credentials (for httpOnly auth cookies).
 * Automatically attempts a silent token refresh once when a request returns 401.
 */

import { API_BASE as BASE } from './api-origin';

const REQUEST_TIMEOUT_MS = 30_000;
const NETWORK_RETRY_DELAY_MS = 500;

export interface ApiSuccess<T> {
  success: true;
  data: T;
}

export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: { field?: string; code?: string; message: string }[];
  };
  timestamp: string;
}

export type ApiResult<T> = ApiSuccess<T> | ApiError;

export function isApiError(r: ApiResult<unknown>): r is ApiError {
  return !r.success;
}

// Endpoints that must never trigger an automatic refresh (would loop or are
// the auth endpoints themselves).
const NO_REFRESH_PATHS = [
  '/auth/refresh',
  '/auth/login',
  '/auth/register',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
];

let refreshPromise: Promise<boolean> | null = null;

// Cross-tab refresh coordination. Two tabs racing /auth/refresh at the same
// instant present the same cookie; the loser triggers the backend's replay
// detector, which revokes the WHOLE session family. `ps_refresh_lock` makes a
// tab wait for the one already refreshing (the cookie jar is shared, so the
// rotated tokens arrive for everyone). `ps_refresh_cooldown` stops a tab from
// hammering the detector after the family was revoked.
const REFRESH_LOCK_KEY = 'ps_refresh_lock';
const REFRESH_LOCK_MS = 1500;
const REFRESH_POLL_MS = 100;
const REFRESH_COOLDOWN_KEY = 'ps_refresh_cooldown';
const REFRESH_COOLDOWN_MS = 60_000;

export const AUTH_EXPIRED_EVENT = 'playerso:auth-expired';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function storageGet(key: string): number {
  try {
    return Number(sessionStorage.getItem(key) || 0);
  } catch {
    return 0;
  }
}

function storageSet(key: string, ts: number) {
  try {
    sessionStorage.setItem(key, String(ts));
  } catch {
    /* storage unavailable — coordination degrades gracefully */
  }
}

function storageRemove(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function refreshOnCooldown(): boolean {
  return Date.now() - storageGet(REFRESH_COOLDOWN_KEY) < REFRESH_COOLDOWN_MS;
}

/** Wait (bounded) for another tab's in-flight refresh to rotate the cookie jar. */
async function waitForOtherTabRefresh(): Promise<void> {
  const deadline = Date.now() + REFRESH_LOCK_MS;
  while (Date.now() < deadline) {
    if (Date.now() - storageGet(REFRESH_LOCK_KEY) >= REFRESH_LOCK_MS) return;
    await sleep(REFRESH_POLL_MS);
  }
}

function signalAuthExpired() {
  try {
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
  } catch {
    /* ignore */
  }
}

/** Fetch with an AbortController timeout. Throws on abort/network failure. */
async function fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** Attempt a silent token refresh via the httpOnly `ps_refresh` cookie. */
function tryRefresh(): Promise<boolean> {
  // Session family already revoked (or a failed attempt just happened) —
  // don't retry for the cooldown window; the replay detector must not be
  // hammered and the user is effectively logged out.
  if (refreshOnCooldown()) return Promise.resolve(false);

  if (refreshPromise) return refreshPromise;

  // Another tab is refreshing right now: wait for it to rotate the shared
  // cookie jar, then let the caller retry with the fresh tokens.
  if (Date.now() - storageGet(REFRESH_LOCK_KEY) < REFRESH_LOCK_MS) {
    return waitForOtherTabRefresh().then(() => true);
  }

  storageSet(REFRESH_LOCK_KEY, Date.now());
  refreshPromise = fetchWithTimeout(`${BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: '' }),
  })
    .then((res) => {
      if (res.ok) {
        storageRemove(REFRESH_COOLDOWN_KEY);
        return true;
      }
      // 401 here means the refresh token was already rotated elsewhere (reuse
      // detection → whole family revoked) or the session genuinely expired.
      // Either way: stop retrying for a while and tell the auth layer.
      storageSet(REFRESH_COOLDOWN_KEY, Date.now());
      signalAuthExpired();
      return false;
    })
    .catch(() => false)
    .finally(() => {
      storageRemove(REFRESH_LOCK_KEY);
      refreshPromise = null;
    });
  return refreshPromise;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  allowRetry = true,
): Promise<ApiResult<T>> {
  const url = `${BASE}${path}`;
  const buildInit = (): RequestInit => ({
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  });

  try {
    // Network/abort failures: retry ONCE after a short delay.
    let res: Response;
    try {
      res = await fetchWithTimeout(url, buildInit());
    } catch {
      if (!allowRetry) throw new Error('network-failed');
      await sleep(NETWORK_RETRY_DELAY_MS);
      res = await fetchWithTimeout(url, buildInit());
    }

    // On 401, attempt a silent refresh (once), then retry the original request.
    if (res.status === 401 && allowRetry && !NO_REFRESH_PATHS.includes(path)) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return request<T>(path, options, false);
      }
    }

    const json = (await res.json().catch(() => null)) as ApiResult<T> | null;
    if (!json) {
      return {
        success: false,
        error: {
          code: 'INTERNAL_ERROR',
          message: 'Unexpected response from server.',
        },
        timestamp: new Date().toISOString(),
      };
    }
    return json;
  } catch {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: 'Could not reach the server. Please check your connection.',
      },
      timestamp: new Date().toISOString(),
    };
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),
  del: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),
};
